/**
 * T01.E02 - Footer
 * The Footer is a module that comprises of a couple of separately
 * specced modules; namely, Footer & ContactUsBar.
 *
 * As such you'll see in this component's folder that it containers
 * `subcomponents` that cleanly make this separation (in addition to
 * further breaking down ContactUsBar into some smaller component atoms).
 *
 * LivePerson (click-to-chat) functionality is also implemented here in
 * the Footer (within ContactUsBar) and is handled in `MessageLivePerson.tsx`.
 */

import React from 'react';
import { compose } from 'recompose';
import { template } from 'lodash';
import ConditionalWrap from 'conditional-wrap';

import LayoutPadding from 'core/components/LayoutPadding';
import { withExperienceEditorContext } from 'core/sitecore/ExperienceEditorContext';
import ExperienceEditorContent from 'core/sitecore/experience-editor/ExperienceEditorContent';
import Cta from 'core/components/Cta';
import * as Typo from 'core/styles/Typography';
import { useAppContext, useTrackEvent } from 'core/hooks';
import TrackLinksWithin from 'core/analytics/TrackLinksWithin';
import { withMemberContext, isAuthenticated } from 'core/sitecore/member';
import { Member } from 'core/api/client/MemberApi';
import { LIVE_ASSIST_BUTTON_TEXT } from 'core/constants';
import { isInSitecorePreviewOrEditMode } from 'core/utils/global-utils';

import * as S from './Footer.style';
import { IFields } from './Footer.sitecore';
import UnauthenticatedFooter from './subcomponents/UnauthenticatedFooter';
import ContactUsBar from './subcomponents/ContactUsBar';
import { useShowComponentWithDelay } from '../hook';
import appView from '../appView';

interface IProps {
	fields: IFields;
	isExperienceEditorActive: boolean;
	member: Member;
}

const Footer = ({ fields, isExperienceEditorActive, member }: IProps) => {
	const { footerLinks } = fields;

	if (typeof fields?.footerLinks === 'undefined' || !fields?.footerLinks) {
		console.log('[for monitoring] - auth footer fields: ', fields?.footerLinks);
	}

	const isMemberAuthenticated = isAuthenticated(member);
	const { isToShow: isToShowAuthenticatedFooter } = useShowComponentWithDelay(
		'footerHasBeenDelayedOnceKey',
	);

	// Analytics spreadsheet:
	// - Section: 'Footer'
	const { trackEvent } = useTrackEvent(
		{
			event_section: 'navigation',
			event_type: 'footer',
			event_title: 'links',
		},
		false,
	);

	const { handleLivePersonModal, sitecoreContext } = useAppContext();
	const maintenanceApiStatus =
		sitecoreContext?.maintenance?.maintenanceApiStatus ?? '';

	const footerYearText = template(fields?.copyrightText?.value)({
		currentYear: new Date().getFullYear(),
	});

	return (
		<div data-target-id="footer">
			{/* 'Authenticated' Footer */}
			{((isMemberAuthenticated && isToShowAuthenticatedFooter) ||
				isExperienceEditorActive ||
				isInSitecorePreviewOrEditMode()) &&
				!appView() && (
					<S.Wrapper data-target-id="footer--authenticated">
						<LayoutPadding>
							<S.LinksContainer>
								<ContactUsBar {...{ fields }} />

								<hr />

								<S.LinkList>
									{footerLinks &&
										footerLinks.map(({ id, fields }) =>
											fields?.footerLink?.value?.text !== undefined ? (
												<S.LinkListItem key={id}>
													{fields?.footerLink?.value?.text ===
													LIVE_ASSIST_BUTTON_TEXT ? (
														<Cta
															href="javascript:void(0);"
															ctaType="unstyled"
															data-target-id="footer--authenticated--link"
															onClick={() => {
																handleLivePersonModal(true);
															}}
														>
															{fields?.footerLink?.value?.text}
														</Cta>
													) : (
														<Cta
															jssLink={fields?.footerLink}
															ctaType="unstyled"
															data-target-id="footer--authenticated--link"
															target="_blank"
															onClick={() => {
																// Analytics spreadsheet:
																// - Section: 'Footer'
																// - ID: 1.2.1; 1.2.2; 1.2.3; 1.2.4; 1.2.5
																trackEvent({
																	event_section: 'navigation',
																	event_type: 'footer',
																	event_title: 'links',
																	event_label: fields.footerLink.value.text?.toLowerCase(),
																	event_trigger: 'click link',
																	event_url: fields.footerLink.value.href,
																});
															}}
														/>
													)}
												</S.LinkListItem>
											) : (
												console.error(
													`[For monitoring] Footer Auth - linktext value is ${fields?.footerLink?.value?.text}`,
												)
											),
										)}
								</S.LinkList>
							</S.LinksContainer>
							<S.DisclaimerContainer>
								<hr />

								<TrackLinksWithin
									targetLinks={[
										[
											'product-disclosure',
											// Analytics spreadsheet:
											// - Section: 'Footer'
											// - ID: 1.2.6
											({ text, link }) =>
												trackEvent({
													event_label: text,
													event_trigger: 'click link',
													event_url: link,
												}),
										],
									]}
								>
									<Typo.Caption
										isRichText
										jss={fields?.generalAdviceDisclaimer}
										color="subdued"
										data-target-id="footer--authenticated--disclaimer"
									/>
								</TrackLinksWithin>
								<Typo.Caption
									isRichText
									color="subdued"
									data-target-id="footer--authenticated--copyright"
								>
									{footerYearText}
								</Typo.Caption>
							</S.DisclaimerContainer>
						</LayoutPadding>
					</S.Wrapper>
				)}

			{/* 'Unauthenticated' Footer */}
			{!isMemberAuthenticated && (
				<ConditionalWrap
					condition={
						isExperienceEditorActive || isInSitecorePreviewOrEditMode()
					}
					wrap={(children: React.ReactNode) => (
						<ExperienceEditorContent
							startCollapsed
							title="Unauthenticated Footer"
						>
							{children}
						</ExperienceEditorContent>
					)}
				>
					<UnauthenticatedFooter {...{ fields }} />
				</ConditionalWrap>
			)}
			{maintenanceApiStatus && (
				<S.MaintenanceApiContainer aria-hidden>
					MaintenanceApiStatus: {maintenanceApiStatus}
				</S.MaintenanceApiContainer>
			)}
		</div>
	);
};

export default compose<IProps, {}>(
	withExperienceEditorContext,
	withMemberContext,
)(Footer);
